import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle, Loader2, Trash2, User } from 'lucide-react';
import { DeletionSuggestion } from '../../../types';

type ConfirmingSpotAction = { id: string; action: 'accept' | 'reject' } | null;

type AdminSpotsPanelProps = {
  deleteSuggestions: DeletionSuggestion[];
  confirmingSpotAction: ConfirmingSpotAction;
  processingId: string | null;
  renderSpotPreview: (suggestion: DeletionSuggestion) => React.ReactNode;
  onBeginSpotAction: (id: string, action: 'accept' | 'reject') => void;
  onCancelSpotAction: () => void;
  onResolveDeletion: () => Promise<void>;
};

const AdminSpotsPanel: React.FC<AdminSpotsPanelProps> = ({
  deleteSuggestions,
  confirmingSpotAction,
  processingId,
  renderSpotPreview,
  onBeginSpotAction,
  onCancelSpotAction,
  onResolveDeletion
}) => {
  const { t } = useTranslation();
  if (deleteSuggestions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 space-y-4 text-center">
        <CheckCircle className="text-emerald-500/20" size={80} />
        <p className="text-sm font-black text-[var(--text-secondary)] uppercase tracking-widest">{t('admin.no_reports', 'Ingen rapporter')}</p>
      </div>
    );
  }

  return (
    <>
      {deleteSuggestions.map((suggestion) => (
        <div key={suggestion.id} className="bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-modal overflow-hidden animate-in slide-in-from-bottom-4 flex flex-col group transition-all duration-500 hover:border-red-500/20">
          {renderSpotPreview(suggestion)}
          <div className="p-8 space-y-5">
            <h3 className="text-[var(--text-primary)] font-black text-base tracking-tight">{suggestion.spotName || t('admin.unnamed_location', 'Navnløs lokasjon')}</h3>

            <div className="flex items-center gap-2 text-[var(--text-muted)]">
              <User size={12} className="shrink-0" />
              <span className="text-tiny font-semibold truncate">
                {suggestion.userName || suggestion.userId}
              </span>
            </div>

            {confirmingSpotAction?.id === suggestion.id ? (
              <div className="bg-red-500/10 border border-red-500/30 p-6 rounded-card space-y-4 animate-in zoom-in-95">
                <div className="flex items-center gap-3 text-red-500">
                  <AlertTriangle size={20} />
                  <p className="text-tiny font-black uppercase tracking-widest">
                    {confirmingSpotAction.action === 'accept' ? t('admin.confirm_delete_spot_all', 'Slette plassen for alle brukere?') : t('admin.confirm_reject_report', 'Avslå rapporten og beholde plassen?')}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={onResolveDeletion}
                    disabled={processingId === suggestion.id}
                    className={`flex-1 py-3.5 rounded-xl text-2xs font-black uppercase flex items-center justify-center gap-2 transition-all shadow-lg active:scale-95 ${confirmingSpotAction.action === 'accept' ? 'bg-red-600 text-white' : 'bg-emerald-600 text-white'}`}
                  >
                    {processingId === suggestion.id ? <Loader2 className="animate-spin" size={14} /> : confirmingSpotAction.action === 'accept' ? <Trash2 size={14} /> : <CheckCircle size={14} />}
                    {t('common.confirm', 'Ja, bekreft')}
                  </button>
                  <button
                    onClick={onCancelSpotAction}
                    className="flex-1 bg-[var(--bg-surface-alt)] text-[var(--text-secondary)] py-3.5 rounded-xl text-2xs font-black uppercase active:scale-95 transition-all"
                  >
                    {t('common.cancel', 'Avbryt')}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="bg-red-500/5 p-5 rounded-card border border-red-500/10">
                  <p className="text-sm text-[var(--text-secondary)] font-medium italic leading-relaxed">"{suggestion.reason}"</p>
                </div>
                <div className="flex gap-4 pt-2">
                  <button onClick={() => onBeginSpotAction(suggestion.id, 'accept')} className="flex-1 bg-red-600/10 hover:bg-red-600 border border-red-500/20 text-red-500 hover:text-white py-4 rounded-2xl text-tiny font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all active:scale-95">
                    <Trash2 size={16} /> {t('admin.delete_spot_btn', 'Slett plassen')}
                  </button>
                  <button onClick={() => onBeginSpotAction(suggestion.id, 'reject')} className="flex-1 bg-[var(--bg-surface-alt)] hover:bg-[var(--bg-surface)] text-[var(--text-secondary)] py-4 rounded-2xl text-tiny font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all active:scale-95">{t('admin.keep_spot_btn', 'Behold den')}</button>
                </div>
              </>
            )}
          </div>
        </div>
      ))}
    </>
  );
};

export default AdminSpotsPanel;
